import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectTransactionDetail,
  selectTransactionDetailRelatedInvoices,
  selectTransactionDetailError
} from 'src/store/selectors/transactionSelectors';
import {
  LOAD_TRANSACTION_DETAIL,
  loadTransactionDetail,
  clearTransactionDetail
} from 'src/store/actions/transactionActions';
import SkeletonBlock from 'src/components/SkeletonBlock';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import SendEmailDialog from 'src/views/taxAccounting/CFDIIssued/SendEmailDialog';
import PaymentFields from 'src/views/client/TransactionDetail/PaymentFields';
import RelatedInvoicesList from 'src/views/client/TransactionDetail/RelatedInvoicesList';
import {
  MUTED_LABEL_COLOR,
  HEADING_TEXT_COLOR,
  HAIRLINE_BORDER_COLOR
} from 'src/views/client/TransactionDetail/styleConstants';
import { CONTENT_CANVAS_COLOR } from '../InvoiceDetailDrawer/styleConstants';

const CARD_SX = {
  border: '1px solid #e0e5ec',
  borderRadius: '4px',
  marginBottom: '18px',
  backgroundColor: '#ffffff'
};

const CARD_HEAD_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '64px',
  padding: '0 20px',
  borderBottom: `1px solid ${HAIRLINE_BORDER_COLOR}`
};

const CARD_TITLE_SX = {
  color: HEADING_TEXT_COLOR,
  fontSize: '17px',
  fontWeight: 800
};

const PaymentDetailDrawer = ({ open, invoiceId, onClose, onMutated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const detail = useSelector(selectTransactionDetail);
  const relatedInvoices = useSelector(selectTransactionDetailRelatedInvoices);
  const hasError = useSelector(selectTransactionDetailError);
  const loading = useSelector(
    isLoadingSelector([LOAD_TRANSACTION_DETAIL.action])
  );

  const [viewingDocument, setViewingDocument] = useState(null);
  const [sendingEmailForInvoiceId, setSendingEmailForInvoiceId] =
    useState(null);

  React.useEffect(() => {
    if (open && invoiceId) {
      dispatch(loadTransactionDetail(invoiceId));
    }
    if (!open) {
      dispatch(clearTransactionDetail());
    }
  }, [dispatch, open, invoiceId]);

  const handleRetry = () => {
    dispatch(loadTransactionDetail(invoiceId));
  };

  const handleViewPdf = () => {
    if (detail?.document?.documentUrl) {
      setViewingDocument({
        documentUrl: detail.document.documentUrl,
        fileName: detail.folio
      });
    }
  };

  const handleSendToClient = () => {
    if (detail?.invoiceDbId) {
      setSendingEmailForInvoiceId(detail.invoiceDbId);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: { width: 680, fontFamily: theme => theme.typography.fontFamily }
      }}
    >
      <Box
        sx={{
          padding: '28px 30px',
          position: 'relative',
          minHeight: '100%',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t('COMMON.CLOSE')}
          sx={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            border: theme => `1px solid ${theme.palette.primary.main}`,
            color: 'primary.main'
          }}
        >
          <CloseIcon />
        </IconButton>

        {hasError ? (
          <Box sx={{ display: 'grid', gap: '14px', paddingTop: '8px' }}>
            <Typography color="error">
              {t('TRANSACTION.DETAIL.LOAD_ERROR')}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleRetry}
              sx={{ justifySelf: 'start' }}
            >
              {t('TRANSACTION.DETAIL.RETRY')}
            </Button>
          </Box>
        ) : loading || !detail ? (
          <Box sx={{ display: 'grid', gap: '18px', paddingTop: '8px' }}>
            <SkeletonBlock width="short" />
            <SkeletonBlock width="medium" tall />
            <SkeletonBlock width="medium" />
          </Box>
        ) : (
          <>
            <Typography
              sx={{
                color: MUTED_LABEL_COLOR,
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              {t('TRANSACTION.DETAIL.PAYMENT_TITLE')}
            </Typography>
            <Typography
              sx={{
                margin: '7px 0 12px',
                color: HEADING_TEXT_COLOR,
                fontSize: '30px',
                fontWeight: 800
              }}
            >
              {detail.folio}
            </Typography>
            <Divider sx={{ marginX: '-30px' }} />

            <Box
              sx={{
                backgroundColor: CONTENT_CANVAS_COLOR,
                marginX: '-30px',
                marginBottom: '-28px',
                padding: '18px 30px 28px',
                flex: 1
              }}
            >
              <Box sx={CARD_SX}>
                <Box sx={CARD_HEAD_SX}>
                  <Typography sx={CARD_TITLE_SX}>
                    {t('TRANSACTION.DETAIL.PAYMENT_DATA')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileOpenIcon />}
                      disabled={!detail.document?.documentUrl}
                      onClick={handleViewPdf}
                    >
                      {t('TRANSACTION.DETAIL.VIEW_COMPLEMENT_PDF')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SendIcon />}
                      disabled={!detail.invoiceDbId}
                      onClick={handleSendToClient}
                    >
                      {t('TRANSACTION.DETAIL.SEND_TO_CLIENT')}
                    </Button>
                  </Box>
                </Box>
                <PaymentFields
                  client={detail.client}
                  contractFolioWithoutPrefix={detail.contractFolioWithoutPrefix}
                  date={detail.date}
                  origin={detail.origin}
                  associatedInvoiceCount={relatedInvoices.length}
                  amountIvaIncluded={detail.amountIvaIncluded}
                  comment={detail.comment}
                  hasContract={Boolean(detail.contractId)}
                />
              </Box>

              <Box sx={{ ...CARD_SX, marginBottom: 0 }}>
                <Box sx={CARD_HEAD_SX}>
                  <Typography sx={CARD_TITLE_SX}>
                    {t('TRANSACTION.DETAIL.ASSOCIATED_INVOICES')}
                  </Typography>
                </Box>
                <RelatedInvoicesList relatedInvoices={relatedInvoices} />
              </Box>
            </Box>
          </>
        )}
      </Box>

      {viewingDocument && (
        <DocumentViewerDialog
          open={Boolean(viewingDocument)}
          onClose={() => setViewingDocument(null)}
          documentUrl={viewingDocument.documentUrl}
          fileName={viewingDocument.fileName}
        />
      )}

      {sendingEmailForInvoiceId && detail && (
        <SendEmailDialog
          open={Boolean(sendingEmailForInvoiceId)}
          onClose={() => {
            setSendingEmailForInvoiceId(null);
            onMutated();
          }}
          id={sendingEmailForInvoiceId}
          clientId={detail.clientId}
        />
      )}
    </Drawer>
  );
};

PaymentDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  invoiceId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onMutated: PropTypes.func.isRequired
};

PaymentDetailDrawer.defaultProps = {
  invoiceId: null
};

export default PaymentDetailDrawer;
